import { DateString, formatDateTimeUrlDate, urlDateToUtcDateTime } from 'common/UrlDate';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import React from 'react';
import { ButtonSizes, PrimaryButton, SecondaryButton } from 'src/shared/design-system/Buttons';
import { FaCircleXMark } from 'src/shared/design-system/FontAwesome';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'src/shared/design-system/Modal';
import { CartOrder } from 'src/store/cart/types';
import styled from 'styled-components';
import { Spacing } from 'web-common/src/shared/styles';

const StyledModal = styled(Modal)`
  padding: ${Spacing.SINGLE_HALF};
  max-height: 80vh;
  width: 480px;
`;

const FooterFlex = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const CancelButton = styled(SecondaryButton)`
  border: none;
  border-radius: 8px;
  padding-left: 16px;
  padding-right: 16px;
  height: 42px;
  font-weight: 500;
`;

const UpdateDeliveryDateButton = styled(PrimaryButton)`
  border-radius: 8px;
  padding-left: 16px;
  padding-right: 16px;
  height: 42px;
  font-weight: 500;
`;

export const AcknowledgeStartNewOrderModal: React.FC<{
  newDeliveryDate?: DateString;
  orders: CartOrder[];
  onCancel: () => void;
  onAccept: () => void;
}> = ({ newDeliveryDate, orders, onCancel, onAccept }) => {
  const foodItems = orders.flatMap(({ lineItems }) => lineItems).map(({ foodItem }) => foodItem);
  const unavailableFoodItems = foodItems.filter(
    ({ availability }) =>
      !availability.find(
        ({ availabilityDate }) => formatDateTimeUrlDate(DateTime.fromISO(availabilityDate)) === newDeliveryDate
      )
  );

  const getModalBody = (unavailableItems) => {
    const deliveryDateTime = newDeliveryDate ? urlDateToUtcDateTime(newDeliveryDate) : null;
    const deliveryDateString = deliveryDateTime
      ? `${deliveryDateTime.weekdayShort}, ${deliveryDateTime.monthShort} ${deliveryDateTime.day}`
      : 'the selected date';

    if (isEmpty(unavailableItems)) {
      return (
        <ModalBody>
          <p>
            You are adding an item for {deliveryDateString}, which does not match the date of the items in your cart.
            Adding this item to cart will change your delivery date for checkout.
          </p>
        </ModalBody>
      );
    }

    return (
      <ModalBody>
        <p>
          Some items in your cart are not available on {deliveryDateString}. These items will be removed from your cart
          if you change your delivery date.
        </p>
        <div className='rounded-lg bg-neutral-50 p-1'>
          {unavailableItems.map((foodItem) => (
            <div key={foodItem.id} className='flex items-center gap-2 p-2'>
              <FaCircleXMark className='mb-0.5 text-radish-200' />
              <div className='truncate font-medium'>{foodItem.name}</div>
            </div>
          ))}
        </div>
      </ModalBody>
    );
  };

  return (
    <StyledModal isShowing showCloseButton onClose={onCancel}>
      <ModalHeader>Change your delivery date?</ModalHeader>
      {getModalBody(unavailableFoodItems)}
      <ModalFooter>
        <FooterFlex>
          <CancelButton size={ButtonSizes.S} onClick={onCancel}>
            <u>Cancel</u>
          </CancelButton>
          <UpdateDeliveryDateButton size={ButtonSizes.S} onClick={onAccept}>
            Update delivery date
          </UpdateDeliveryDateButton>
        </FooterFlex>
      </ModalFooter>
    </StyledModal>
  );
};
